import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import { Helmet } from "react-helmet";

import Header from "./Header";
import Footer from "./Footer";
import "./../../assets/css/layout.css";
import "./../../assets/css/common.scss";
import { CustomI18nextContext } from "../../contexts/CustomI18nextContext";
import { PageContext } from "../../contexts/PageContext";

const Layout = ({
  title,
  description,
  imageMetaTag,
  children,
  limeSignUpButton,
}) => {
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          author
          siteUrl
        }
      }
    }
  `);

  const awsBucketUrl =
    "https://zipmex-production-zipmex-assets.s3.ap-southeast-1.amazonaws.com/common/";

  const mapSiteCountry = function (country) {
    return country === "global" || country === "au"
      ? "EN"
      : country.toUpperCase();
  };

  useEffect(() => {
    document.body.classList.remove("stop-scrolling");
  }, []);

  const { t, lang, siteCountry } = useContext(CustomI18nextContext);
  const { pagePath, latestPosts, footerMenus } = useContext(PageContext);

  const siteCountryCode = mapSiteCountry(siteCountry);

  return React.useMemo(
    () => (
      <>
        <Helmet htmlAttributes={{ lang: t("common.seoLang") }}>
          <script src="https://www.googleoptimize.com/optimize.js?id=OPT-K3RG49G"></script>
          <title>{title}</title>
          <meta name="description" content={description} />

          <meta property="og:title" content={title} />
          <meta property="og:type" content="website" />
          <meta property="og:description" content={description} />
          <meta
            property="og:image"
            content={
              imageMetaTag
                ? `${data.site.siteMetadata.siteUrl}${imageMetaTag}`
                : `${awsBucketUrl}AW_Web_banner_${siteCountryCode}.png`
            }
          />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta
            name="twitter:creator"
            content={data.site.siteMetadata.author}
          />
          <meta
            name="twitter:image"
            content={
              imageMetaTag
                ? `${data.site.siteMetadata.siteUrl}${imageMetaTag}`
                : `${awsBucketUrl}AW_Web_banner_${siteCountryCode}.png`
            }
          />
          <meta name="google-play-app" content={"app-id=com.zipmex"} />

          <link
            rel="alternate"
            href={`https://zipmex.com/th${pagePath}`}
            hrefLang="th-th"
          />
          <link
            rel="alternate"
            href={`https://zipmex.com/th/en${pagePath}`}
            hrefLang="en-th"
          />
          <link
            rel="alternate"
            href={`https://zipmex.com/id${pagePath}`}
            hrefLang="id-id"
          />
          <link
            rel="alternate"
            href={`https://zipmex.com/id/en${pagePath}`}
            hrefLang="en-id"
          />
          <link
            rel="alternate"
            href={`https://zipmex.com/au${pagePath}`}
            hrefLang="en-au"
          />
          <link
            rel="alternate"
            href={`https://zipmex.com${pagePath}`}
            hrefLang="x-default"
          />
          <link
            rel="alternate"
            href={`https://zipmex.com${pagePath}`}
            hrefLang="en"
          />
        </Helmet>
        <Header
          latestPosts={latestPosts}
          siteCountry={siteCountry}
          t={t}
          limeSignUpButton={limeSignUpButton}
        />

        <main>{children}</main>

        <Footer
          siteCountry={siteCountry}
          lang={lang}
          t={t}
          footerMenus={footerMenus}
        />
      </>
    ),
    []
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;
